import React from 'react';

// ── Design tokens (matches Dashboard.jsx) ─────────────────────────────────────
const T = {
  bg:          '#0f1117',
  surface:     '#161b27',
  raised:      '#1a2035',
  border:      'rgba(255,255,255,0.07)',
  borderStrong:'rgba(255,255,255,0.13)',
  borderFocus: 'rgba(99,102,241,0.5)',
  text:        '#e8eaf0',
  textMuted:   '#6b7280',
  textDim:     '#9ca3af',
  accent:      '#6366f1',
  accentLight: 'rgba(99,102,241,0.15)',
  success:     '#10b981',
  successLight:'rgba(16,185,129,0.12)',
  danger:      '#ef4444',
  dangerLight: 'rgba(239,68,68,0.12)',
  radius:      '14px',
  radiusSm:    '9px',
};

const STEPS = [
  {
    number: '1',
    title: 'Open Canvas Settings',
    detail: 'Log in to Canvas and click your avatar → Account → Settings.',
  },
  {
    number: '2',
    title: 'Generate a developer token',
    detail: 'Scroll to Approved Integrations and click + New Access Token. Give it any name and leave expiry blank.',
  },
  {
    number: '3',
    title: 'Paste your token below',
    detail: 'Copy the token shown by Canvas and paste it into the field below.',
  },
];

const INJECTED_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { overflow-x: hidden; max-width: 100vw; }

  .dq-ob-textarea::placeholder { color: #374151; }
  .dq-ob-textarea:focus {
    outline: none;
    border-color: rgba(99,102,241,0.5) !important;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }
  .dq-ob-btn:hover:not(:disabled) {
    box-shadow: 0 6px 20px rgba(99,102,241,0.4) !important;
    transform: translateY(-1px);
  }
  .dq-ob-btn:active:not(:disabled) { transform: translateY(0); }

  @keyframes dq-spin { to { transform: rotate(360deg); } }
  @keyframes dq-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }

  @media (max-width: 600px) {
    .dq-ob-card   { padding: 24px 18px !important; border-radius: 12px !important; }
    .dq-ob-steps  { gap: 12px !important; }
    .dq-ob-step   { padding: 12px 14px !important; }
    .dq-ob-btn    { font-size: 15px !important; padding: 14px 18px !important; }
  }

  @media (max-width: 400px) {
    .dq-ob-card   { padding: 20px 14px !important; border-radius: 10px !important; }
    .dq-ob-textarea { font-size: 13px !important; }
  }
`;

export default function Onboarding({
  token,
  setToken,
  onSave,
  saving,
  error,
  success,
}) {
  return (
    <>
      <style>{INJECTED_CSS}</style>

      {/* ── Page wrapper ─────────────────────────────────────────────────── */}
      <div style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '32px 16px 64px',
        fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
      }}>
        {/* ── Card ─────────────────────────────────────────────────────── */}
        <div
          className="dq-ob-card"
          style={{
            width: '100%',
            maxWidth: 560,
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: '36px 40px',
            boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
            animation: 'dq-fadein 0.25s ease',
          }}
        >

          {/* ── Headline ───────────────────────────────────────────────── */}
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, letterSpacing: '-0.5px', marginBottom: 8, lineHeight: 1.2 }}>
            Connect your Canvas account
          </h1>
          <p style={{ fontSize: 14, color: T.textMuted, lineHeight: 1.6, marginBottom: 28 }}>
            Paste a Canvas access token to sync your assignments, deadlines, and priorities — all in one clean dashboard.
          </p>

          {/* ── Steps ──────────────────────────────────────────────────── */}
          <div
            className="dq-ob-steps"
            style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 }}
          >
            {STEPS.map((s) => (
              <div
                key={s.number}
                className="dq-ob-step"
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '13px 16px',
                  background: T.raised,
                  border: `1px solid ${T.border}`,
                  borderRadius: T.radiusSm,
                }}
              >
                {/* Step number badge */}
                <div style={{
                  width: 24, height: 24, borderRadius: '50%',
                  background: T.accentLight,
                  border: '1px solid rgba(99,102,241,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, color: '#a5b4fc',
                  flexShrink: 0, marginTop: 1,
                }}>
                  {s.number}
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 3 }}>
                    {s.title}
                  </div>
                  <div style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>
                    {s.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Token input ────────────────────────────────────────────── */}
          <div style={{ marginBottom: 6 }}>
            <label style={{
              display: 'block',
              fontSize: 11, fontWeight: 700, color: T.textMuted,
              textTransform: 'uppercase', letterSpacing: '0.06em',
              marginBottom: 8,
            }}>
              Canvas access token
            </label>
            <textarea
              className="dq-ob-textarea"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your Canvas access token here…"
              rows={4}
              style={{
                width: '100%',
                padding: '12px 14px',
                background: T.raised,
                border: `1px solid ${T.borderStrong}`,
                borderRadius: T.radiusSm,
                color: T.text,
                fontSize: 14,
                fontFamily: '"DM Mono", "Fira Code", "Menlo", monospace',
                lineHeight: 1.6,
                resize: 'vertical',
                transition: 'border-color 0.15s, box-shadow 0.15s',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* ── Reassurance note ───────────────────────────────────────── */}
          <p style={{ fontSize: 11, color: T.textMuted, marginBottom: 18, lineHeight: 1.5 }}>
            🔒 Your token is used only to sync your assignments. You can remove access anytime from Canvas Settings → Approved Integrations.
          </p>

          {/* ── Connect button ─────────────────────────────────────────── */}
          <button
            className="dq-ob-btn"
            onClick={onSave}
            disabled={saving || !token.trim()}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              width: '100%',
              padding: '13px 18px',
              background: (saving || !token.trim())
                ? 'rgba(99,102,241,0.4)'
                : 'linear-gradient(135deg, #6366f1, #818cf8)',
              border: 'none',
              borderRadius: T.radiusSm,
              color: '#fff',
              fontSize: 15,
              fontWeight: 700,
              cursor: (saving || !token.trim()) ? 'not-allowed' : 'pointer',
              fontFamily: '"DM Sans", ui-sans-serif, system-ui, sans-serif',
              boxShadow: (saving || !token.trim()) ? 'none' : '0 4px 14px rgba(99,102,241,0.35)',
              transition: 'all 0.15s',
              letterSpacing: '-0.1px',
            }}
          >
            {saving ? (
              <>
                <span style={{
                  width: 15, height: 15,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'dq-spin 0.7s linear infinite',
                }} />
                Connecting…
              </>
            ) : (
              <>
                <span>🔗</span> Connect Canvas
              </>
            )}
          </button>

          {/* ── Success message ────────────────────────────────────────── */}
          {success && (
            <div style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 14px',
              background: T.successLight,
              border: '1px solid rgba(16,185,129,0.2)',
              borderRadius: T.radiusSm,
              fontSize: 13,
              fontWeight: 600,
              color: '#34d399',
              animation: 'dq-fadein 0.2s ease',
            }}>
              ✅ Connected successfully — loading your dashboard…
            </div>
          )}

          {/* ── Error message ──────────────────────────────────────────── */}
          {error && (
            <div style={{
              marginTop: 14,
              padding: '10px 14px',
              background: T.dangerLight,
              border: '1px solid rgba(239,68,68,0.2)',
              borderRadius: T.radiusSm,
              fontSize: 13,
              color: '#fca5a5',
              lineHeight: 1.45,
              animation: 'dq-fadein 0.2s ease',
            }}>
              {error}
            </div>
          )}

          {/* ── Footer note ────────────────────────────────────────────── */}
          <p style={{ marginTop: 20, fontSize: 11, color: T.textMuted, textAlign: 'center', lineHeight: 1.5 }}>
            We only read your coursework. We never modify your Canvas account.
          </p>
        </div>
      </div>
    </>
  );
}
